import { useState } from 'react'

import { Icon } from '@loadsmart/miranda-react'

import {
  StyledRunningLabel,
  StyledSpinner,
  StyledStep,
  StyledSteps,
  StyledStepSpacer,
  StyledTyping,
  StyledWork,
  StyledWorkToggle,
} from './AnswerProgress.styles'

/*
 * Our own versions of the library's `TypingIndicator` and `Work`. Those play
 * Lottie animations through a WebAssembly player, and the page's CSP does not
 * allow WebAssembly to compile ('wasm-unsafe-eval'). These are CSS only.
 */

type WorkState = 'active' | 'complete'

/** Shown while a reply is on its way but has nothing to draw yet. */
export function TypingDots() {
  return (
    <StyledTyping role="status" aria-label="Writing an answer">
      <span />
      <span />
      <span />
    </StyledTyping>
  )
}

type AgentWorkProps = {
  /** `active` while the agent is still working through this run. */
  state: WorkState
  steps: readonly { label: string; state: WorkState }[]
}

/**
 * The agent's account of what it did. It stays open while the run is active
 * and folds away behind a row the user can open once the run is done.
 */
export function AgentWork({ state, steps }: AgentWorkProps) {
  const [isOpenState, setIsOpenState] = useState(false)
  const isActive = state === 'active'
  const isOpen = isActive || isOpenState

  return (
    <StyledWork data-testid="ai-chat-work">
      <StyledWorkToggle
        type="button"
        disabled={isActive}
        aria-expanded={isOpen}
        onClick={() => setIsOpenState(open => !open)}
      >
        {isActive ? (
          <StyledSpinner aria-hidden="true" />
        ) : (
          <Icon name={isOpen ? 'caret-down' : 'caret-right'} size="14px" />
        )}
        {isActive ? 'Working…' : 'Steps done'}
      </StyledWorkToggle>
      {isOpen && (
        <StyledSteps>
          {steps.map((step, index) => (
            <StyledStep key={index}>
              {step.state === 'active' ? (
                <>
                  <StyledStepSpacer />
                  <StyledRunningLabel>{step.label}</StyledRunningLabel>
                </>
              ) : (
                <>
                  <Icon name="check" size="14px" />
                  <span>{step.label}</span>
                </>
              )}
            </StyledStep>
          ))}
        </StyledSteps>
      )}
    </StyledWork>
  )
}
